import { DataGrid, DataGridColumn, DataGridRef, Spinner, Toggle } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { useFileRecords } from '../../queries/use-file-records';
import { useFirmContext } from '../../providers/FirmProvider';
import { FileRecordNew } from '../../types/file-record';
import { OtherScheduleFileOverview } from '../../types/other-schedule';
import { WorksheetFileOverview } from '../../types/worksheet';
import { ROW_SIZES } from '../../constants';
import { useDispatch } from 'react-redux';
import { selectFiles } from '../../slices/filesSlice';
import ViewErrorDialog from '../../features/files/ViewErrorDialog';
import FilesBreadcrumbs from '../../features/files/FilesBreadcrumbs';
import ErrorSplashScreen from '../../pages/ErrorSplashScreen';
import FileRow from './FileRow';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { DocumentIcon } from '@heroicons/react/24/outline';

const columns: DataGridColumn[] = [
    { header: '', width: '15px', alignment: 'center' },
    { header: 'Name', width: '2.5fr', alignment: 'left' },
    { header: 'Items', width: '0.5fr', alignment: 'center' },
    { header: 'Last Updated', width: '1fr', alignment: 'left' },
    { header: '', width: '0.5fr', alignment: 'right' },
];

type FilesTableProps = {
    readOnly?: boolean;
    restrictingFolderId?: string;
    toResetFolderId: string;
    initialFolderId: string;
    attachedFiles?: OtherScheduleFileOverview[] | WorksheetFileOverview[];
    gridRef: React.MutableRefObject<DataGridRef>;
    filterQuery?: string;
    sortQuery?: string;
    searchQuery?: string;
    isFlat?: boolean;
    onUploading: (uploading: boolean) => void;
    onDetailsToggle: (id: string) => void;
    onFolderChange: (id: string) => void;
    onEmptyStateNewFile: () => void;
};

function FilesTable({
    readOnly = false,
    toResetFolderId,
    initialFolderId,
    gridRef,
    filterQuery,
    sortQuery,
    searchQuery,
    attachedFiles,
    restrictingFolderId,
    isFlat = false,
    onUploading,
    onDetailsToggle,
    onFolderChange,
    onEmptyStateNewFile,
}: FilesTableProps): JSX.Element {
    const { id: firmId } = useFirmContext();
    const [page, setPage] = useState(1);
    const [flat, setFlat] = useState(isFlat);
    const [limit, setLimit] = useState(ROW_SIZES[0]);
    const [viewErrorRecord, setViewErrorDocument] = useState<FileRecordNew | undefined>();
    const fileRecordsQuery = useFileRecords(page, limit, restrictingFolderId, initialFolderId, filterQuery, sortQuery, searchQuery, flat);

    const dispatch = useDispatch();

    useEffect(() => {
        return (): void => {
            dispatch(selectFiles([]));
        };
    }, [dispatch]);

    function handleFolderChange(id: string): void {
        onFolderChange(id);
        setPage(1);
        gridRef.current?.resetPagination();
    }

    function selectFileRecords(ids: string[]): void {
        const selectedFiles = fileRecordsQuery.data?.data.records.filter((g) => ids.includes(g.id)) ?? [];
        dispatch(selectFiles(selectedFiles));
    }

    /**
     * Check if a file record is used on a worksheet/other schedule entry
     * @param fileRecordId
     * @returns
     */
    function isReferenced(fileRecordId: string): boolean {
        const record = attachedFiles?.find((f) => f.id === fileRecordId);
        return record !== undefined && record.entries.length > 0;
    }

    if (fileRecordsQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (fileRecordsQuery.isError || !fileRecordsQuery.isSuccess) {
        // If folderId is not valid, reset back to initial starting folder
        if (fileRecordsQuery.error?.response?.status === 404) {
            onFolderChange(toResetFolderId);
            return <></>;
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to firm folder"
                handleClick={(): void => onFolderChange(firmId)}
                leftArrow={true}
            />
        );
    }

    const { data: files, totalItems } = fileRecordsQuery.data;
    const showEmpty = !searchQuery;
    return (
        <>
            <div className="flex mb-4">
                {files.path && (
                    <div className="w-[80%]">
                        <FilesBreadcrumbs path={fileRecordsQuery.data.data.path} onLevelSelect={handleFolderChange} sync />
                    </div>
                )}
                <div className="border-b flex items-center ml-auto flex-1 justify-end gap-1">
                    <p className="text-sm text-gray-700">Flat view</p>
                    <Toggle
                        defaultEnabled={flat}
                        onToggle={(e): void => {
                            setFlat(e);
                            setPage(1);
                            gridRef.current.resetPagination();
                        }}
                        type="short"
                    />
                </div>
            </div>
            <DataGrid
                ref={gridRef}
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={totalItems}
                isLoading={fileRecordsQuery.isFetching}
                onPageChange={setPage}
                onRowSizeChange={setLimit}
                onSelect={selectFileRecords}
                emptyMessage={
                    showEmpty ? (
                        <EmptyStateNew
                            icon={<DocumentIcon />}
                            actionText="Add a File"
                            helpLink="https://learn.cimplico.com/docs/files-overview"
                            helpText="Upload any supporting files needed for your workpapers."
                            title="Add a file"
                            onClick={onEmptyStateNewFile}
                            disableActionButton={readOnly}
                        />
                    ) : (
                        <NoResults search={searchQuery} />
                    )
                }
            >
                {files.records.map((record) => {
                    return (
                        <FileRow
                            id={record.id}
                            key={record.id}
                            record={record}
                            showPath={flat || Boolean(searchQuery)}
                            isReferenced={isReferenced(record.id)}
                            onDetailsToggle={onDetailsToggle}
                            onFolderSelect={handleFolderChange}
                            onUploading={onUploading}
                            readOnly={readOnly}
                        />
                    );
                })}
            </DataGrid>
        </>
    );
}
export default FilesTable;
